// Score feedback: a short Web Audio click + an optional haptic buzz.
// Both opt-out via localStorage; reads are sync so a tap can fire
// without a render round-trip.
//
// Named SFX layer: per-effect plays go through `playNamedSfx` first,
// which looks up an admin-uploaded asset (kind="sfx") bound to that
// slot. If nothing is bound, the synthesized fallback in this file
// runs — keeping the game audible out of the box.

import { playNamedSfx } from "@/lib/sfx-catalog";
import type { SfxSlot } from "@/lib/queries";

const SOUND_KEY = "cornholio.sound.enabled";
const HAPTIC_KEY = "cornholio.haptic.enabled";

// When true, the synth gate (`audible`) plays even if the in-game sound
// toggle is off. Set only for the duration of an admin PREVIEW so the
// designer can audition a default sound regardless of the play-surface
// mute state. Never left on outside previewSlotSynth's try/finally.
let previewForce = false;
function audible(): boolean {
  return previewForce || isSoundEnabled();
}

function read(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  const v = window.localStorage.getItem(key);
  if (v === "true") return true;
  if (v === "false") return false;
  return fallback;
}

export function isSoundEnabled(): boolean {
  return read(SOUND_KEY, true);
}
export function isHapticEnabled(): boolean {
  return read(HAPTIC_KEY, true);
}

export function setSoundEnabled(on: boolean): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SOUND_KEY, String(on));
}
export function setHapticEnabled(on: boolean): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(HAPTIC_KEY, String(on));
}

let ctx: AudioContext | null = null;
function audioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const Ctor =
    window.AudioContext ||
    (window as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

// Soft, percussive blip — short envelope, low-mid pitch. Designed not to
// annoy a backyard crowd if multiple games are scoring at once.
export function playScoreSound(intent: "score" | "win" | "undo" = "score"): void {
  if (!audible()) return;
  const ac = audioContext();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume().catch(() => {});

  const now = ac.currentTime;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = intent === "win" ? "triangle" : "sine";
  const startFreq =
    intent === "win" ? 660 : intent === "undo" ? 320 : 540;
  const endFreq = intent === "win" ? 990 : intent === "undo" ? 220 : 420;
  osc.frequency.setValueAtTime(startFreq, now);
  osc.frequency.exponentialRampToValueAtTime(endFreq, now + 0.12);

  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.18, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);

  osc.connect(gain);
  gain.connect(ac.destination);
  osc.start(now);
  osc.stop(now + 0.2);
}

// ----- Virtual bag SFX -----
//
// Synthesized via Web Audio API so we ship zero asset bytes for them.
// All four short-effect calls are no-ops when sound is disabled.

// Short low thump for a bag landing on the slab.
export function playBagLand(): void {
  playNamedSfx("bag_land", synthBagLand);
}
function synthBagLand(): void {
  if (!audible()) return;
  const ac = audioContext();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume().catch(() => {});
  const now = ac.currentTime;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(180, now);
  osc.frequency.exponentialRampToValueAtTime(60, now + 0.09);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.35, now + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
  osc.connect(gain);
  gain.connect(ac.destination);
  osc.start(now);
  osc.stop(now + 0.2);
}

// Heavier softer thud for hitting the lawn (miss).
export function playBagOnGround(): void {
  playNamedSfx("bag_on_ground", synthBagOnGround);
}
function synthBagOnGround(): void {
  if (!audible()) return;
  const ac = audioContext();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume().catch(() => {});
  const now = ac.currentTime;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(120, now);
  osc.frequency.exponentialRampToValueAtTime(45, now + 0.12);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.22, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  osc.connect(gain);
  gain.connect(ac.destination);
  osc.start(now);
  osc.stop(now + 0.28);
}

// Celebratory "3-pointer" / "in the hole" sound: a quick ascending
// triad with a bell-like overtone on top.
export function playBagInHole(): void {
  playNamedSfx("bag_in_hole", synthBagInHole);
}
function synthBagInHole(): void {
  if (!audible()) return;
  const ac = audioContext();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume().catch(() => {});
  const now = ac.currentTime;
  // Ascending C-E-G arpeggio.
  const notes = [523.25, 659.25, 783.99];
  notes.forEach((freq, i) => {
    const t = now + i * 0.08;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.26, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    o.connect(g);
    g.connect(ac.destination);
    o.start(t);
    o.stop(t + 0.4);
  });
  // Bell shimmer at top.
  const t2 = now + 0.16;
  const bellOsc = ac.createOscillator();
  const bellGain = ac.createGain();
  bellOsc.type = "sine";
  bellOsc.frequency.setValueAtTime(1568, t2); // G6
  bellGain.gain.setValueAtTime(0.0001, t2);
  bellGain.gain.exponentialRampToValueAtTime(0.15, t2 + 0.008);
  bellGain.gain.exponentialRampToValueAtTime(0.0001, t2 + 0.7);
  bellOsc.connect(bellGain);
  bellGain.connect(ac.destination);
  bellOsc.start(t2);
  bellOsc.stop(t2 + 0.75);
  vibrate([20, 30, 20]);
}

// Continuous filtered-noise slide. Returns a stop fn; call it when the
// bag stops sliding so we don't leak a sustained oscillator.
let activeSlide: { stop: () => void } | null = null;

export function startBagSlide(): void {
  if (!audible()) return;
  if (activeSlide) return;
  const ac = audioContext();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume().catch(() => {});

  // White noise → bandpass → quiet gain. Bandpass center around 900Hz
  // gives a paper-on-wood texture rather than open noise.
  const bufferSize = Math.floor(ac.sampleRate * 0.5);
  const buffer = ac.createBuffer(1, bufferSize, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  const noise = ac.createBufferSource();
  noise.buffer = buffer;
  noise.loop = true;

  const filter = ac.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 900;
  filter.Q.value = 1.2;

  const gain = ac.createGain();
  gain.gain.value = 0.05;

  noise.connect(filter);
  filter.connect(gain);
  gain.connect(ac.destination);
  noise.start();

  activeSlide = {
    stop() {
      const t = ac.currentTime;
      gain.gain.cancelScheduledValues(t);
      gain.gain.setValueAtTime(gain.gain.value, t);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
      setTimeout(() => {
        try {
          noise.stop();
        } catch {
          /* already stopped */
        }
      }, 100);
    },
  };
}

export function stopBagSlide(): void {
  if (!activeSlide) return;
  activeSlide.stop();
  activeSlide = null;
}

export function vibrate(pattern: number | number[]): void {
  if (!isHapticEnabled()) return;
  if (typeof navigator === "undefined") return;
  if (typeof navigator.vibrate !== "function") return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // ignore
  }
}

// One-shot helpers callers can drop into onClick handlers.
export function scoreFeedback(): void {
  playScoreSound("score");
  vibrate(12);
}
export function winFeedback(): void {
  // Match-complete riff. Custom upload binds to "game_win" slot.
  playNamedSfx("game_win", () => playScoreSound("win"));
  vibrate([20, 40, 20, 40, 60]);
}
export function undoFeedback(): void {
  playScoreSound("undo");
  vibrate(8);
}

// Throw-release accent — fires the instant the bag leaves the
// player's finger. Synthesized fallback is intentionally subtle (a
// short whoosh-ish sweep) so most rooms don't notice it; admins
// upload a punchier custom clip for the "throw_release" slot.
export function playThrowRelease(): void {
  playNamedSfx("throw_release", synthThrowRelease);
}
function synthThrowRelease(): void {
  if (!audible()) return;
  const ac = audioContext();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume().catch(() => {});
  const now = ac.currentTime;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(320, now);
  osc.frequency.exponentialRampToValueAtTime(120, now + 0.12);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.08, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
  osc.connect(gain);
  gain.connect(ac.destination);
  osc.start(now);
  osc.stop(now + 0.18);
}

// Round-start fanfare — a clean rising arpeggio on triangle. Played
// when the per-round dialog appears so the player has an audible cue
// that the next round is starting. Admins can override this with an
// uploaded clip bound to the "round_start" slot in Admin Designer.
export function playRoundStart(): void {
  playNamedSfx("round_start", synthRoundStart);
}
function synthRoundStart(): void {
  if (!audible()) return;
  const ac = audioContext();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume().catch(() => {});
  const now = ac.currentTime;
  // Three ascending tones (C5, E5, G5) — a major triad arpeggio.
  // Each note ~0.13s, slight overlap for a connected "fanfare" feel.
  const notes = [523.25, 659.25, 783.99];
  notes.forEach((freq, i) => {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(freq, now + i * 0.1);
    gain.gain.setValueAtTime(0.0001, now + i * 0.1);
    gain.gain.exponentialRampToValueAtTime(0.12, now + i * 0.1 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.1 + 0.16);
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.start(now + i * 0.1);
    osc.stop(now + i * 0.1 + 0.2);
  });
  vibrate(15);
}

// Round-winner accent — a brighter, shorter ding that punctuates the
// scoring side's number in the round result banner. Distinct from the
// game-complete winFeedback (which is the bigger end-of-match riff).
// Overridable via the "round_winner" slot in Admin Designer.
export function playRoundWinner(): void {
  playNamedSfx("round_winner", synthRoundWinner);
}
function synthRoundWinner(): void {
  if (!audible()) return;
  const ac = audioContext();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume().catch(() => {});
  const now = ac.currentTime;
  // Two-note "ding-dong": G5 → C6.
  const notes = [783.99, 1046.5];
  notes.forEach((freq, i) => {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(freq, now + i * 0.09);
    gain.gain.setValueAtTime(0.0001, now + i * 0.09);
    gain.gain.exponentialRampToValueAtTime(0.15, now + i * 0.09 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.09 + 0.18);
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.start(now + i * 0.09);
    osc.stop(now + i * 0.09 + 0.2);
  });
  vibrate(18);
}

// Four-bagger fanfare — fires when a side puts up 12 in a round (all
// four bags in the hole). The biggest per-round cue: a rising C-major
// run capped by a bright bell + a triumphant triple-buzz. Admins can
// override the "four_bagger" slot with an uploaded clip (e.g. an
// announcer shouting "4 Bagger!").
export function playFourBagger(): void {
  playNamedSfx("four_bagger", synthFourBagger);
}
function synthFourBagger(): void {
  if (!audible()) return;
  const ac = audioContext();
  if (!ac) return;
  if (ac.state === "suspended") ac.resume().catch(() => {});
  const now = ac.currentTime;
  // Fast ascending C-major run C5-E5-G5-C6 for a "climbing" feel.
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((freq, i) => {
    const t = now + i * 0.07;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.3, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
    o.connect(g);
    g.connect(ac.destination);
    o.start(t);
    o.stop(t + 0.36);
  });
  // Bright bell shimmer on top (C7) to cap it.
  const tb = now + 0.28;
  const bell = ac.createOscillator();
  const bg = ac.createGain();
  bell.type = "sine";
  bell.frequency.setValueAtTime(2093, tb);
  bg.gain.setValueAtTime(0.0001, tb);
  bg.gain.exponentialRampToValueAtTime(0.18, tb + 0.008);
  bg.gain.exponentialRampToValueAtTime(0.0001, tb + 0.9);
  bell.connect(bg);
  bg.connect(ac.destination);
  bell.start(tb);
  bell.stop(tb + 0.95);
  vibrate([30, 40, 30, 40, 30]);
}

// ----- Admin preview -----
//
// Maps each game-sound slot to its built-in synthesized fallback so
// the Admin Designer's "Game Sounds" panel can audition the DEFAULT
// for a slot (independent of any uploaded clip bound to it). To
// preview the BOUND clip, the panel plays the asset URL directly.
const SLOT_SYNTH: Record<SfxSlot, () => void> = {
  round_start: synthRoundStart,
  round_winner: synthRoundWinner,
  throw_release: synthThrowRelease,
  bag_land: synthBagLand,
  bag_in_hole: synthBagInHole,
  bag_on_ground: synthBagOnGround,
  four_bagger: synthFourBagger,
  game_win: () => playScoreSound("win"),
};

// Play a slot's built-in synth default, forcing audibility for the
// duration so an admin can preview even if the play-surface sound
// toggle is off. previewForce is reset synchronously in finally (the
// synth schedules its oscillators synchronously, then returns).
export function previewSlotSynth(slot: SfxSlot): void {
  previewForce = true;
  try {
    SLOT_SYNTH[slot]?.();
  } finally {
    previewForce = false;
  }
}
